import { FormErrors } from 'components/molecules/form/typing';
import { ObjectPaths } from 'utils/objectPaths';
import { getValueFromObject } from 'utils/valueFromObject';

export const ERROR_REQUIRED = 'Input required';

export const ERROR_MIN_LENGTH = (minLength: number) =>
  `Minimum ${minLength} characters`;

export const ERROR_MAX_LENGTH = (maxLength: number) =>
  `Maximum ${maxLength} characters`;

export const ERROR_MIN = (min: number | string) => `Enter ${min} or greater`;

export const ERROR_MAX = (max: number | string) => `Enter ${max} or less`;

export const PLACEHOLDERS = {
  EMAIL: 'user@workoptima.com',
  TELEPHONE: '1234567890',
  URL: 'https://app.workoptima.com',
};

export const SUPPORTED_FILE_FORMAT_DESC =
  'Supported file formats: PDF, JPG, TIFF';

export function GET_ARRAY_FIELD_NAME<TObject, TForm>(
  local: ObjectPaths<TObject>,
  prefix: ObjectPaths<TForm>
): ObjectPaths<TForm> {
  return `${prefix}.${local}` as ObjectPaths<TForm>;
}

export function transformObjectErrorsIntoStringErrors(
  startPath: string,
  errorObj: FormErrors
): string[] {
  const result: string[] = [];

  const traverse = (obj: any, currentPath: string) => {
    // If we find an object with "type" and "message", treat it as a leaf node.
    if (obj && typeof obj === 'object' && 'type' in obj && 'message' in obj) {
      if (currentPath.startsWith(startPath) || startPath === '') {
        result.push(currentPath);
      }
      return; // Do not traverse any deeper into this branch.
    }

    // Otherwise, continue traversing the object's keys.
    for (const key of Object.keys(obj)) {
      const fullPath = currentPath ? `${currentPath}.${key}` : key;
      traverse(obj[key], fullPath);
    }
  };

  traverse(errorObj, '');

  // Filter to ensure we only include paths that start with the base and are deeper than the base.
  return result.filter(
    (path) =>
      path !== startPath &&
      (path.startsWith(`${startPath}.`) || startPath === '')
  );
}

export type ErrorWithMessage = { path: string; message: string; value: any };

export function transformObjectErrorsIntoErrorsWithMessages(
  startPath: string,
  errorObj: FormErrors,
  values: Record<string, any>
): ErrorWithMessage[] {
  const result: ErrorWithMessage[] = [];

  const traverse = (obj: any, currentPath: string) => {
    // If we find an object with "type" and "message", treat it as a leaf node.
    if (obj && typeof obj === 'object' && 'type' in obj && 'message' in obj) {
      if (currentPath.startsWith(startPath) || startPath === '') {
        result.push({
          path: currentPath,
          message: obj.message,
          value: getValueFromObject(values, currentPath),
        });
      }
      return; // Do not traverse any deeper into this branch.
    }

    // Otherwise, continue traversing the object's keys.
    for (const key of Object.keys(obj)) {
      const fullPath = currentPath ? `${currentPath}.${key}` : key;
      traverse(obj[key], fullPath);
    }
  };

  traverse(errorObj, '');

  // Filter to ensure we only include paths that start with the base and are deeper than the base.
  return result.filter((errorWithMessage) =>
    // if no start path, then assume we want all
    !startPath
      ? true
      : errorWithMessage.path !== startPath &&
        (errorWithMessage.path.startsWith(`${startPath}.`) || startPath === '')
  );
}

export const GET_FAKE_FIELDNAME = (fieldName: string) =>
  `${fieldName}_FAKE_FIELD`;

export function TRANSLATE_STRING_TO_ARRAY_BY_DELIMITER(
  delimiter: string,
  value: string
): string[] {
  return value.split(delimiter);
}

export function TRANSLATE_ARRAY_TO_STRING_BY_DELIMITER(
  delimiter: string,
  value: string[]
): string {
  return value.join(delimiter);
}

export function ENCODE_PILLS(str: string[]): string {
  return str.map((v) => v.replaceAll(';', ';;')).join(';');
}

export function DECODE_ESCAPED_PILLS(str: string): string[] {
  const result = [];
  let current = '';

  for (let i = 0; i < str.length; i++) {
    if (str[i] === ';') {
      if (str[i + 1] === ';') {
        current += ';';
        i++; // skip second semi-colon
      } else {
        result.push(current);
        current = '';
      }
    } else {
      current += str[i];
    }
  }

  result.push(current);

  return result;
}
